/**
 * author：brian
 * date：2022 04 24
 * 用途:在登录跳转之前获取info接口数据 存储到vuex中 另外 存储方式的数据要与前辈们的存储数据需要一致
 */
import router from './router';
import store from './store';
import i18n from '@/lang';
import bus from '@/utils/bus';
import { generateTitle } from './utils/i18n';
import { userLogout } from './api/modules/indexPage';
import { Message } from 'element-ui';
import { syncThemeFromServer, applyThemeColor, DEFAULT_COLOR } from '@/utils/theme';
import { mineAvatar } from '@/api/modules/material';
import Cookies from 'js-cookie';
router.beforeEach(async (to, from, next) => {
    // 只有在login页面不会获取unit
    if (to.path !== '/login' && !to.meta.win && !to.meta.noAuth) {
        store.dispatch('unit').catch(() => {});
    }

    // 激活左侧菜单对应的项
    bus.$emit('setActiveTab', to);
    if (to.meta.windowTitle) return next();
    if (to.name) {
        document.title = i18n.t('system.Cold_chain_system');
    } else {
        document.title = i18n.t('system.Cold_chain_system');
    }
    if (to.path === '/login') {
        // 进入登录页时清除上个用户残留的缓存（主题色、头像等）
        // 覆盖"直接关闭浏览器"场景：下次访问登录页时也是干净的默认状态
        window.localStorage.removeItem('app_theme_primary_color');
        Cookies.remove('app_theme_primary_color');
        applyThemeColor(DEFAULT_COLOR);
        store.commit('SET_AVATAR', '');
        store.commit('SET_USERINFO', { scope: null, user: {}, switchScope: [] });
        store.commit('SET_TREE_DATA', []);
        store.commit('SET_TREE_USER_ID', null);
        next();
    } else if (!to.meta.noAuth) {
        // 从服务器同步主题色偏好
        // 从登录页跳转时 await（确保登录后立即应用用户自己的主题色，避免读到上个用户残留的缓存）
        // 其他路由跳转时非阻塞（避免每次跳转都卡住）
        const isFromLogin = from.path === '/login';
        if (isFromLogin) {
            await syncThemeFromServer().catch(() => {});
        } else {
            syncThemeFromServer().catch(() => {});
        }
        try {
        // 获取当前用户信息
        let { result } = await store.dispatch('user/pageInfo');
        if (!result.funcs) {
            userLogout();
            Message.error('暂无访问权限');
            return;
        }
        store.commit('SET_CURRENT_SYSTEM_MENU', result.funcs);
        // 将菜单的 nameZh/nameEn/code 按 url === path 匹配，填充到当前 to.meta 上
        // to.meta 引用 vue-router 内部 record.meta，修改会持久化，后续导航同一路由时自动携带
        fillRouteMeta(to, result.funcs);
        store.commit('SET_USERINFO', result.userRsp);
        // 兼容个人用户（scope 为 null）和企业用户
        const scopeType = result.userRsp.scope ? result.userRsp.scope.type : 0;
        store.commit('SET_USER_TYPE', scopeType);
        // 账号改造：存储用户类型（PERSONAL / ENTERPRISE / ENTERPRISE_ROOT），用于菜单过滤
        // MineController 有 @SnakeCaseResponse，User 字段经 @FieldNaming 序列化为 snake_case
        const userType = result.userRsp.user && result.userRsp.user.userType
            ? result.userRsp.user.userType
            : 'ENTERPRISE';
        store.commit('SET_ACCOUNT_USER_TYPE', userType);
        localStorage.setItem('userType', userType);

        // 加载头像到 vuex
        // 从登录页跳转时强制请求（避免读到上个用户残留的持久化头像）；其他时候非阻塞
        if (isFromLogin || !store.getters.avatar) {
            mineAvatar().then(({ result }) => {
                store.commit('SET_AVATAR', result && result.avatar || '');
            }).catch(() => {});
        }

        // 首次登录强制改密兜底检查（防止用户直接访问页面绕过登录页跳转）
        // MineController 有 @SnakeCaseResponse，User 字段经 @FieldNaming 序列化为 snake_case
        const isFirstLogin = result.userRsp.user && result.userRsp.user.isFirstLogin;
        if (isFirstLogin === 1 && to.path !== '/force-change-password') {
            return next('/force-change-password');
        }

        const auth = {};
        // Roasilan 2023-07-04
        // 将权限放入一hashTable 方便查询 因为查询频次太高了 总不能每次都来个两三层循环
        result.auths.forEach(item => {
            auth[item] = true;
        });
        store.commit('SET_AUTH', auth);

        // 获取当前用户的进度id（非阻塞，失败不影响跳转）
        store.dispatch('downLoad/getAllUserProgress').catch(() => {});

        // 只有从登陆到首页才调用树接口，或用户切换后持久化树数据已过期需强制刷新
        const currentUserId = result.userRsp.user && result.userRsp.user.id;
        const needRefreshTree = from.path === '/login' || to.hash === '#refreshTree'
            || !store.state.SchemaTree || store.state.SchemaTree.length === 0
            || (currentUserId && store.state.treeUserId !== currentUserId);
        if (needRefreshTree) {
            // 确保 store.state.type 已正确设置（前面 SET_USER_TYPE 已 commit）
            // type=2/3 为平台/代理管理员，传 all=true 查看全部节点；其他用户不传 all
            let query = {};
            if (store.state.type === 3 || store.state.type === 2) {
                query = { idEdit: 0, all: true };
            } else {
                query = { idEdit: 0 };
            }
            let treeData = await store.dispatch('user/getTreeData', query);

            // 空数组保护：避免 result.item[0] 抛错导致缓存不更新（保持旧账号数据）
            const treeItems = (treeData.result && treeData.result.item) || [];
            if (treeItems.length > 0 && treeItems[0].sceneCode === 'all_node') {
                treeItems[0].label = i18n.t('public.All');
            }
            store.commit('SET_TREE_DATA', treeItems);
            store.commit('SET_TREE_USER_ID', currentUserId);
        }
        } catch (err) {
            console.error('[router-guard] error:', err);
        }
    }
    next();
});

/**
 * 将菜单数据的 nameZh/nameEn/code 按 url === path 匹配，填充到 to.meta 上
 * to.meta 引用 vue-router 内部 record.meta，修改会持久化到路由记录
 * 菜单数据为树形结构（父菜单 url 为空，子菜单在 sub 数组中带有 url）
 */
function fillRouteMeta (to, menus) {
    if (!to || !to.path || !Array.isArray(menus)) return;
    const found = findMenuByPath(menus, to.path);
    if (found) {
        if (!to.meta) to.meta = {};
        to.meta.nameZh = found.nameZh;
        to.meta.nameEn = found.nameEn;
        to.meta.code = found.code;
    }
}

// 在菜单树中按 url === path 查找菜单项
function findMenuByPath (items, path) {
    if (!Array.isArray(items)) return null;
    for (const item of items) {
        if (item.url === path) return item;
        if (item.sub && item.sub.length > 0) {
            const found = findMenuByPath(item.sub, path);
            if (found) return found;
        }
    }
    return null;
}
